import { useEffect, useRef, useState } from 'react';
import styles from './AlbumsScreen.module.css';
import EvenInArcadia from '../components/Albums/EvenInArcadia';
import TakeMeBackToEden from '../components/Albums/TakeMeBackToEden';
import Sundowning from '../components/Albums/Sundowning';
import ThisPlaceWillBecomeYourTomb from '../components/Albums/ThisPlaceWillBecomeYourTomb';
import LyricsDisplay from '../components/Albums/LyricsDisplay';

import BgEvenInArcadia from '../assets/albums/even_in_arcadia.webp';
import BgTMBTE from '../assets/albums/tmbte.webp';
import BgTPWBYT from '../assets/albums/tpwbyt.jpg';
import BgSundowning from '../assets/albums/sundowning.png';

import TMBTELogo from '../assets/albums/tmbte_logo.webp';

const albumLogos = { tmbte: TMBTELogo };

function AlbumsScreen() {
  const albumRefs = useRef([]);
  const eiaRef = useRef(null);
  const tmbteRef = useRef(null);
  const tpwbytRef = useRef(null);
  const sundowningRef = useRef(null);

  const [background, setBackground] = useState(BgEvenInArcadia);
  const [nextBackground, setNextBackground] = useState(null);
  const [ripple, setRipple] = useState(null);
  const [playingAlbum, setPlayingAlbum] = useState('eia');
  const [albumTitle, setAlbumTitle] = useState({ line1: 'Even in', line2: 'Arcadia' });
  const [titleVisible, setTitleVisible] = useState(true);
  const [logoVisible, setLogoVisible] = useState(false);

  useEffect(() => {
    [BgTMBTE, BgTPWBYT, BgSundowning, BgEvenInArcadia, TMBTELogo].forEach((src) => {
      const img = new Image();
      img.src = src;
      img.decode?.().catch(() => {});
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) entry.target.classList.add('visible');
          });
        },
        { threshold: 0.1 }
      );
      albumRefs.current.forEach((ref) => { if (ref) observer.observe(ref); });
      return () => observer.disconnect();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (titleVisible && albumLogos[playingAlbum]) {
      const timer = setTimeout(() => setLogoVisible(true), 200);
      return () => clearTimeout(timer);
    } else {
      setLogoVisible(false);
    }
  }, [titleVisible, playingAlbum]);

  const handleAlbumClick = (newBg, ref, albumId, title) => {
    if (newBg === background) return;
    if (!ref?.current) return;

    const rect = ref.current.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;

    setLogoVisible(false);
    setNextBackground(newBg);
    setRipple({ x, y });
    setTitleVisible(false);

    setTimeout(() => {
      setBackground(newBg);
      setNextBackground(null);
      setRipple(null);
      setPlayingAlbum(albumId);
      setAlbumTitle(title);
      setTimeout(() => setTitleVisible(true), 100);
    }, 1400);
  };

  const albums = [
    {
      id: 'eia',
      bg: BgEvenInArcadia,
      title: { line1: 'Even in', line2: 'Arcadia' },
      ref: eiaRef,
      refSetter: (el) => { albumRefs.current[0] = el; eiaRef.current = el; },
      delay: '0s',
      component: EvenInArcadia,
    },
    {
      id: 'tmbte',
      bg: BgTMBTE,
      title: { line1: 'Take Me Back', line2: 'To Eden' },
      ref: tmbteRef,
      refSetter: (el) => { albumRefs.current[1] = el; tmbteRef.current = el; },
      delay: '0.15s',
      component: TakeMeBackToEden,
    },
    {
      id: 'tpwbyt',
      bg: BgTPWBYT,
      title: { line1: 'This Place Will', line2: 'Become Your Tomb' },
      ref: tpwbytRef,
      refSetter: (el) => { albumRefs.current[2] = el; tpwbytRef.current = el; },
      delay: '0.3s',
      component: ThisPlaceWillBecomeYourTomb,
    },
    {
      id: 'sundowning',
      bg: BgSundowning,
      title: { line1: '', line2: 'Sundowning' },
      ref: sundowningRef,
      refSetter: (el) => { albumRefs.current[3] = el; sundowningRef.current = el; },
      delay: '0.45s',
      component: Sundowning,
    },
  ];

  return (
    <section
      className={styles.hero}
      style={{ backgroundImage: `url(${background})` }}
    >
      {nextBackground && ripple && (
        <div
          className={styles.rippleOverlay}
          style={{
            backgroundImage: `url(${nextBackground})`,
            '--ripple-x': `${ripple.x}px`,
            '--ripple-y': `${ripple.y}px`,
          }}
        />
      )}

      {albumLogos[playingAlbum] && (
        <img
          src={albumLogos[playingAlbum]}
          alt=""
          className={`${styles.albumLogo} ${logoVisible ? styles.albumLogoVisible : ''}`}
        />
      )}

      <div className={styles.textContainer}>
        <div className={`${styles.title} ${titleVisible ? styles.titleVisible : styles.titleHidden}`}>
          <p>{albumTitle.line1}</p>
          <p>{albumTitle.line2}</p>
        </div>
        <LyricsDisplay albumId={playingAlbum} titleVisible={titleVisible} />
      </div>

      <div className={styles.albumsContainer}>
        {albums.map((album) => {
          const Component = album.component;
          return (
            <Component
              key={album.id}
              albumRef={album.refSetter}
              delay={album.delay}
              isPlaying={playingAlbum === album.id}
              onClick={() => handleAlbumClick(album.bg, album.ref, album.id, album.title)}
            />
          );
        })}
      </div>
    </section>
  );
}

export default AlbumsScreen;